import React, { useEffect, useState } from "react";

import { FormControl, FormHelperText, IconButton } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import {
  RemoveCircleOutline,
  AddCircleOutline,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import classnames from "classnames";

import {
  handleGetConvertValue,
  handleSaveConvertValue,
  Metric,
  CUBIC_FOOT_TO_CUBIC_METER_RATIO,
  DEFAULT_MAX_LENGTH,
  KILOGRAM_TO_POUND_RATIO,
} from "../../../util/converts";
import { numberIsUndefinedOrNull } from "../../../util/numberIsUndefinedOrNull";
import Input, { InputProps } from "../Input";
import useStyles from "./styles";
import { colors } from "@zapalacorp/tokens";

const FormControlCustomized = withStyles(() => ({
  root: {
    display: "flex",
    justifyContent: "flex-end",
  },
}))(FormControl);

type MeasurementInputRange = {
  code?: string;
  decimalPlaces?: number;
};

interface InputRangeProps extends InputProps {
  label?: string;
  value: number | string;
  range?: number;
  formControlClassName?: string;
  containerClassName?: string;
  measurementInputRange?: MeasurementInputRange;
  decimalPlacesMaxLength?: number;
  change?: (value: number) => void;
  onIncrease?: (value: number) => void;
  onDecrease?: (value: number) => void;
  disabled?: boolean;
  minValue?: number;
  maxValue?: number;
  onlyIntValues?: boolean;
  error?: boolean;
  errorMessage?: string;
}

const InputRange = ({
  label,
  value,
  range,
  change,
  onIncrease,
  onDecrease,
  measurementInputRange,
  decimalPlacesMaxLength,
  disabled = false,
  formControlClassName,
  containerClassName,
  minValue = 1,
  maxValue,
  onlyIntValues = false,
  error = false,
  errorMessage,
  required,
  ...rest
}: InputRangeProps) => {
  const [stateTextValue, setStateTextValue] = useState<string>(
    String(
      handleGetConvertValue(
        Number(value),
        measurementInputRange?.code,
        measurementInputRange?.decimalPlaces
      )
    )
  );
  const [stateValue, setStateValue] = useState<number>(Number(value));
  const [numberIsInvalid, setNumberIsInvalid] = useState<boolean>(false);
  const [contentToInput, setContentToInput] = useState<boolean>(false);
  const [onFocused, setOnFocused] = useState<boolean>(false);

  const classes = useStyles({ numberIsInvalid })({ disabled });

  const getMaxLength = () =>
    decimalPlacesMaxLength ?? measurementInputRange?.decimalPlaces ?? DEFAULT_MAX_LENGTH;

  const updateCurrentValue = (value: number | string): boolean => {
    if (
      !measurementInputRange?.code ||
      numberIsUndefinedOrNull(measurementInputRange?.decimalPlaces)
    )
      return true;

    if (String(Number(value) % 1)?.length >= getMaxLength()) return true;

    return false;
  };

  useEffect(() => {
    if (value !== stateValue) setStateValue(Number(value));
  }, [value]);

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  useEffect(() => {
    if (String(stateValue) !== stateTextValue && !onFocused)
      setStateTextValue(
        String(
          handleGetConvertValue(
            Number(stateValue),
            measurementInputRange?.code,
            measurementInputRange?.decimalPlaces
          )
        )
      );
  }, [stateValue]);

  const renderLabel = () => {
    if (label) {
      return (
        <span className={classes.label}>
          {label}
          {renderRequiredLabel()}
        </span>
      );
    }

    return null;
  };

  const onChangeState = (inputValue: string | number) => {
    if (onChange) onChange(inputValue);
    else setStateValue(Number(inputValue));
  };

  const onChange = (value: string | number) => {
    const nValue = Number(value);

    if (change) change(nValue);
  };

  const handleIncreaseValue = (value: string | number) => {
    const nValue = Number(value);

    setNumberIsInvalid(false);
    if (onIncrease) onIncrease(nValue);
    else if (change) change(nValue);
  };

  const handleDecreaseValue = (value: string | number) => {
    const nValue = Number(value);

    setNumberIsInvalid(false);
    if (onDecrease) onDecrease(nValue);
    else if (change) change(nValue);
  };

  const handleRangeValue = () => {
    if (range) return range;

    switch (measurementInputRange?.code) {
      case Metric["ft³"]:
      case Metric.ft3:
        return CUBIC_FOOT_TO_CUBIC_METER_RATIO;
      case Metric["m³"]:
      case Metric.m3:
        return 0.05;
      case Metric.lb:
      case Metric.lbs:
        return KILOGRAM_TO_POUND_RATIO;
      default:
        return 1;
    }
  };

  const renderContentInfo = () => {
    if (contentToInput) {
      return (
        <Input
          fullWidth={false}
          formControlClassName={classes.formControl}
          className={classes.input}
          value={stateTextValue}
          focused
          autoFocus
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            try {
              const value = Number(event.target.value);

              if (onlyIntValues && !Number.isInteger(value)) return;

              const convertedValue = handleSaveConvertValue(
                event.target.value,
                measurementInputRange?.code
              );

              setStateTextValue(event.target.value);

              if (
                value < minValue ||
                (maxValue ? value > maxValue : false) ||
                Number.isNaN(convertedValue) ||
                (onlyIntValues ? value % 1 !== 0 : false)
              )
                setNumberIsInvalid(true);
              else {
                setNumberIsInvalid(false);
                onChangeState(convertedValue);
              }
            } catch (err) {
              setNumberIsInvalid(true);
            }
          }}
          error={numberIsInvalid}
          name={rest.name}
          rightIcon={measurementInputRange?.code}
          onBlur={() => {
            if (updateCurrentValue(stateTextValue)) {
              setStateTextValue(Number(stateTextValue)?.toFixed(getMaxLength()));
            }
            setOnFocused(false);
          }}
          onFocus={() => setOnFocused(true)}
        />
      );
    }

    return (
      <span className={classes.info}>
        {stateTextValue}
        {measurementInputRange?.code || ""}
      </span>
    );
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText
          className={classes.formHelper}
          data-cy="errors"
          style={{ whiteSpace: "nowrap" }}
        >
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }
  };

  const minusButtonIsDisabled =
    disabled || handleRangeValue() <= -1 || stateValue - handleRangeValue() < minValue;
  const plusButtonIsDisabled =
    disabled ||
    handleRangeValue() <= -1 ||
    (maxValue ? stateValue + handleRangeValue() > maxValue : false);

  return (
    <FormControlCustomized className={formControlClassName}>
      {renderLabel()}
      <div className={classnames(containerClassName, classes.mainDiv)}>
        <IconButton
          disabled={minusButtonIsDisabled}
          onClick={() => {
            if (Number(value) > 0) handleDecreaseValue(Number(value) - handleRangeValue());
          }}
          className={classes.iconButton}
        >
          <RemoveCircleOutline
            htmlColor={minusButtonIsDisabled ? "#CCDEEE" : "#F97066"}
            className={classes.icon}
          />
        </IconButton>
        <div
          onClick={() => {
            if (!disabled) setContentToInput(true);
          }}
          onBlur={() => {
            if (!disabled) setContentToInput(false);
          }}
          className={classes.containerContent}
          data-cy={rest["data-cy"]}
        >
          {renderContentInfo()}
        </div>
        <IconButton
          disabled={plusButtonIsDisabled}
          onClick={() => {
            if (Number(value) >= 0) handleIncreaseValue(Number(value) + handleRangeValue());
          }}
          className={classes.iconButton}
        >
          <AddCircleOutline
            htmlColor={plusButtonIsDisabled ? "#CCDEEE" : colors.brandBlue500}
            className={classes.icon}
          />
        </IconButton>
      </div>
      {renderError()}
    </FormControlCustomized>
  );
};

export default InputRange;
